import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/MockAuthContext';
import { useNavigate } from 'react-router-dom';
import { dataApi } from '../services/dataService';
import UserSaleItems from '../components/UserSaleItems';
import { formatWithdrawalSource, formatWithdrawalEffect, withdrawalSourceBadgeClass } from '../utils/withdrawalSource';
import { formatStockBalance } from '../utils/stockFlow';

interface Item {
  item_id: number;
  item_name: string;
  category: string;
  initial_stock: number;
  current_stock: number;
  unit_price: number;
  buying_price?: number;
  date_added: string;
  sku?: string;
  total_allocated: number;
  total_added: number;
}

interface Stall {
  stall_id: number;
  stall_name: string;
  location: string;
  manager: string;
}

const Inventory: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [items, setItems] = useState<Item[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [showLowStock, setShowLowStock] = useState(false);
  const [categories, setCategories] = useState<string[]>([]);
  const [stalls, setStalls] = useState<Stall[]>([]);
  const [selectedStall, setSelectedStall] = useState<number | ''>('');
  const [showDistributeModal, setShowDistributeModal] = useState(false);
  const [showAddStockModal, setShowAddStockModal] = useState(false);
  const [showWithdrawModal, setShowWithdrawModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedItem, setSelectedItem] = useState<Item | null>(null);
  const [salesData, setSalesData] = useState<any[]>([]);
  // Keyed by source so the concurrent fetches on mount don't clear each other's
  // warning. A stale or failed read must stay visible until it actually succeeds.
  const [loadWarnings, setLoadWarnings] = useState<Record<string, string>>({});
  const noteLoad = useCallback((source: string, message: string | null) => {
    setLoadWarnings(prev => {
      if (!message) {
        if (!(source in prev)) return prev;
        const next = { ...prev };
        delete next[source];
        return next;
      }
      if (prev[source] === message) return prev;
      return { ...prev, [source]: message };
    });
  }, []);
  const loadWarning = Object.values(loadWarnings)[0] ?? null;
  const [salesAggregates, setSalesAggregates] = useState<{
    byItem: Record<number, number>;
    byItemStall: Record<string, number>;
  }>({ byItem: {}, byItemStall: {} });
  const [editFormData, setEditFormData] = useState({
    item_name: '',
    category: '',
    unit_price: '',
    buying_price: '',
    initial_stock: '',
    total_added: '',
    current_stock: ''
  });
  const [distributionData, setDistributionData] = useState({
    item_id: 0,
    distributions: [] as Array<{ stall_id: string; quantity: string }>,
    notes: ''
  });
  const [addStockQuantity, setAddStockQuantity] = useState('');
  const [withdrawQuantity, setWithdrawQuantity] = useState('');
  const [withdrawReason, setWithdrawReason] = useState('');
  // 'central' or a stall distribution_id (number as string)
  const [withdrawSource, setWithdrawSource] = useState<'central' | string>('central');
  // distributions for the item currently being withdrawn from (populated when modal opens)
  const [withdrawItemDistributions, setWithdrawItemDistributions] = useState<any[]>([]);
  const [expandedItemId, setExpandedItemId] = useState<number | null>(null);
  const [itemDistributions, setItemDistributions] = useState<any[]>([]);
  const [showEditDistModal, setShowEditDistModal] = useState(false);
  const [editingDist, setEditingDist] = useState<any>(null);
  const [editDistQty, setEditDistQty] = useState('');
  const [editDistStallId, setEditDistStallId] = useState<string>('');
  const [isRefreshingDist, setIsRefreshingDist] = useState(false);
  const [isRefreshingAdditions, setIsRefreshingAdditions] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [itemStockAdditions, setItemStockAdditions] = useState<any[]>([]);
  const [itemStockWithdrawals, setItemStockWithdrawals] = useState<any[]>([]);
  const [isRefreshingWithdrawals, setIsRefreshingWithdrawals] = useState(false);

  // Per-stall allocation map: item_id -> [{stall_name, quantity_allocated}]
  const [stallAllocMap, setStallAllocMap] = useState<Map<number, { stall_name: string; quantity_allocated: number }[]>>(new Map());

  // Withdraw from distribution state
  const [showWithdrawFromDistModal, setShowWithdrawFromDistModal] = useState(false);
  const [withdrawFromDist, setWithdrawFromDist] = useState<any>(null);
  const [withdrawFromDistQty, setWithdrawFromDistQty] = useState('');

  // Undo toast after a successful "withdraw from distribution"
  const [undoToast, setUndoToast] = useState<{
    withdrawalId: number;
    qty: number;
    stallName: string;
    itemId: number;
  } | null>(null);
  const undoTimerRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);

  const fetchAllStallAllocations = useCallback(async (itemList: any[], options?: { merge?: boolean }) => {
    if (user?.role !== 'admin' || itemList.length === 0) return;
    try {
      // One request for the whole list instead of one per item. Refreshing a
      // single item after a withdrawal still asks for just that item.
      let results: { item_id: number; distributions: any[] }[];

      if (itemList.length === 1) {
        const item = itemList[0];
        const res = await dataApi.getDistributions(item.item_id).catch(() => ({ distributions: [] }));
        results = [{ item_id: item.item_id, distributions: res.distributions || [] }];
      } else {
        const res = await dataApi.getDistributions();
        const grouped = new Map<number, any[]>();
        for (const dist of res.distributions || []) {
          const bucket = grouped.get(dist.item_id);
          if (bucket) bucket.push(dist);
          else grouped.set(dist.item_id, [dist]);
        }
        results = itemList.map(item => ({
          item_id: item.item_id,
          distributions: grouped.get(item.item_id) || []
        }));
      }

      setStallAllocMap(prev => {
        const map = options?.merge
          ? new Map(prev)
          : new Map<number, { stall_name: string; quantity_allocated: number }[]>();
        results.forEach(({ item_id, distributions }) => {
          map.set(item_id, distributions);
        });
        return map;
      });
    } catch (error) {
      console.error('Error fetching stall allocations:', error);
    }
  }, [user?.role]);

  const fetchItems = useCallback(async (options?: { skipStallAllocations?: boolean }) => {
    try {
      // For non-admin users, pass their stall_id to get only distributed stock
      const stallId = user?.role !== 'admin' && user?.stall_id ? user.stall_id : undefined;
      console.log(`[Inventory] Fetching items. Role: ${user?.role}, stall_id: ${user?.stall_id}, effective stallId: ${stallId}`);

      const response = await dataApi.getInventory(stallId);
      console.log(`[Inventory] Received ${response.items?.length || 0} items from API`);

      const sortedItems = [...(response.items || [])].sort((a, b) =>
        a.item_name.localeCompare(b.item_name, undefined, { sensitivity: 'base' })
      );
      setItems(sortedItems);
      noteLoad(
        'items',
        (response as any).stale
          ? 'Showing the last synced copy — these numbers may be behind. Reconnect and refresh before distributing or withdrawing.'
          : null
      );
      if (user?.role === 'admin' && !options?.skipStallAllocations) {
        fetchAllStallAllocations(sortedItems);
      }
      return sortedItems;
    } catch (error) {
      // Keep whatever is already on screen rather than blanking the list, and
      // say so, so a dropped request is never mistaken for missing stock.
      console.error('Error fetching items:', error);
      noteLoad('items', error instanceof Error ? error.message : 'Couldn\'t refresh the inventory. Check your connection and try again.');
      return [];
    } finally {
      setLoading(false);
    }
  }, [user?.role, user?.stall_id, fetchAllStallAllocations, noteLoad]);

  const fetchSales = useCallback(async () => {
    // Admins read sold counts from the aggregates; the full sales list is only
    // needed to filter by the signed-in seller, so don't pull it for them.
    const isAdmin = user?.role === 'admin';
    try {
      const [salesResponse, aggregates] = await Promise.all([
        isAdmin ? Promise.resolve({ sales: [] as any[] }) : dataApi.getSales(),
        dataApi.getSalesAggregates()
      ]);
      setSalesData(salesResponse.sales);
      setSalesAggregates(aggregates);
      noteLoad('sales', null);
    } catch (error) {
      console.error('Error fetching sales:', error);
      noteLoad('sales', error instanceof Error ? error.message : 'Couldn\'t load sales totals, so the sold figures below may be incomplete.');
    }
  }, [user?.role, noteLoad]);

  const fetchStalls = useCallback(async () => {
    try {
      const response = await dataApi.getStalls();
      setStalls(response.stalls);
      noteLoad('stalls', null);
    } catch (error) {
      // An incomplete stall list would quietly hide a stall from the distribute
      // and withdraw pickers, so say it out loud instead of showing a short list.
      console.error('Error fetching stalls:', error);
      noteLoad('stalls', error instanceof Error ? error.message : 'Couldn\'t load the stall list, so some stalls may be missing from the distribute and withdraw options.');
    }
  }, [noteLoad]);

  useEffect(() => {
    fetchItems();
    fetchCategories();
    fetchStalls();
    fetchSales();

    // Add event listeners for real-time updates (fast path)
    const handleInventoryUpdate = () => {
      console.log('🔄 Real-time inventory update detected');
      fetchItems();
    };
    
    const handleSalesUpdate = () => {
      console.log('🔄 Real-time sales update detected');
      fetchSales();
    };
    
    window.addEventListener('inventory-updated', handleInventoryUpdate);
    window.addEventListener('sales-updated', handleSalesUpdate);

    // Fallback polling (increased to 30s to reduce unnecessary requests)
    const interval = setInterval(() => {
      console.log('📡 Polling inventory data (fallback)');
      fetchItems();
      fetchSales();
    }, 30000);

    return () => {
      clearInterval(interval);
      window.removeEventListener('inventory-updated', handleInventoryUpdate);
      window.removeEventListener('sales-updated', handleSalesUpdate);
    };
  }, [fetchItems, fetchSales, fetchStalls]);

  const fetchItemDistributions = useCallback(async (itemId: number) => {
    if (user?.role !== 'admin') return;
    setIsRefreshingDist(true);
    try {
      const response = await dataApi.getDistributions(itemId);
      setItemDistributions(response.distributions || []);
    } catch (error) {
      console.error('Error fetching distributions:', error);
    } finally {
      setIsRefreshingDist(false);
    }
  }, [user?.role]);

  const fetchItemStockAdditions = useCallback(async (itemId: number) => {
    setIsRefreshingAdditions(true);
    try {
      const response = await dataApi.getStockAdditions(itemId);
      setItemStockAdditions(response.additions || []);
      noteLoad('additions', null);
    } catch (error) {
      // Clear the panel rather than leave the previous item's rows under this
      // item's name, and say why it's empty so it doesn't look like lost history.
      console.error('Error fetching stock additions:', error);
      setItemStockAdditions([]);
      noteLoad('additions', error instanceof Error ? error.message : 'Couldn\'t load the stock addition history for this item.');
    } finally {
      setIsRefreshingAdditions(false);
    }
  }, [noteLoad]);

  const fetchItemStockWithdrawals = useCallback(async (itemId: number) => {
    setIsRefreshingWithdrawals(true);
    try {
      const response = await dataApi.getStockWithdrawals(itemId);
      setItemStockWithdrawals(response.withdrawals || []);
      noteLoad('withdrawals', null);
    } catch (error) {
      console.error('Error fetching stock withdrawals:', error);
      setItemStockWithdrawals([]);
      noteLoad('withdrawals', error instanceof Error ? error.message : 'Couldn\'t load the withdrawal history for this item.');
    } finally {
      setIsRefreshingWithdrawals(false);
    }
  }, [noteLoad]);

  const notifyServiceWorkerToClearSupabaseCache = () => {
    if ('serviceWorker' in navigator && navigator.serviceWorker.controller) {
      navigator.serviceWorker.controller.postMessage({ type: 'CLEAR_SUPABASE_CACHE' });
    }
  };

  const refreshAfterMutation = useCallback(async (affectedItemId?: number) => {
    notifyServiceWorkerToClearSupabaseCache();
    const sortedItems = await fetchItems({ skipStallAllocations: true });
    if (user?.role === 'admin') {
      if (affectedItemId != null) {
        await fetchAllStallAllocations([{ item_id: affectedItemId }], { merge: true });
        fetchAllStallAllocations(sortedItems);
      } else {
        await fetchAllStallAllocations(sortedItems);
      }
    }
    if (affectedItemId != null) {
      const updatedItem = sortedItems.find((item: any) => item.item_id === affectedItemId);
      if (updatedItem) {
        setSelectedItem(updatedItem);
      }
    }
    if (expandedItemId) {
      await fetchItemDistributions(expandedItemId);
      if (user?.role === 'admin') {
        await Promise.all([
          fetchItemStockAdditions(expandedItemId),
          fetchItemStockWithdrawals(expandedItemId),
        ]);
      }
    }
    return sortedItems;
  }, [
    fetchItems,
    fetchAllStallAllocations,
    fetchItemDistributions,
    fetchItemStockAdditions,
    fetchItemStockWithdrawals,
    expandedItemId,
    user?.role,
  ]);

  const toggleExpand = (itemId: number) => {
    setExpandedItemId(prev => prev === itemId ? null : itemId);
  };

  useEffect(() => {
    if (expandedItemId) {
      fetchItemDistributions(expandedItemId);
      if (user?.role === 'admin') {
        fetchItemStockAdditions(expandedItemId);
        fetchItemStockWithdrawals(expandedItemId);
      }
    } else {
      setItemDistributions([]);
      setItemStockAdditions([]);
      setItemStockWithdrawals([]);
    }
  }, [expandedItemId, fetchItemDistributions, fetchItemStockAdditions, fetchItemStockWithdrawals, user?.role]);

  const fetchCategories = async () => {
    try {
      // Mock categories from inventory data
      const response = await dataApi.getInventory();
      const categories = [...new Set(response.items.map(item => item.category))].sort((a, b) =>
        a.localeCompare(b, undefined, { sensitivity: 'base' })
      );
      setCategories(categories);
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  // Distribution functionality is handled by the modal

  const handleDistributionSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedItem) return;

    // Validate distributions
    if (distributionData.distributions.length === 0) {
      alert('Please add at least one stall distribution');
      return;
    }

    const totalDistributed = distributionData.distributions.reduce((sum, dist) => {
      return sum + (parseInt(dist.quantity) || 0);
    }, 0);

    if (totalDistributed > selectedItem.current_stock) {
      alert(`Total quantity (${totalDistributed}) exceeds available stock (${selectedItem.current_stock})`);
      return;
    }

    setIsSubmitting(true);
    try {
      // Validate all distributions first
      const validDistributions = distributionData.distributions
        .filter(dist => parseInt(dist.quantity) > 0 && dist.stall_id)
        .map(dist => ({
          stall_id: parseInt(dist.stall_id),
          quantity: parseInt(dist.quantity)
        }));

      const totalDistributed = validDistributions.reduce((sum, d) => sum + d.quantity, 0);

      if (totalDistributed > selectedItem.current_stock) {
        alert(`Total quantity (${totalDistributed}) exceeds available stock (${selectedItem.current_stock})`);
        setIsSubmitting(false);
        return;
      }

      // Call API to distribute to all stalls at once
      await dataApi.distributeStock({
        item_id: selectedItem.item_id,
        distributions: validDistributions,
        notes: distributionData.notes
      });

      setShowDistributeModal(false);
      setDistributionData({
        item_id: 0,
        distributions: [],
        notes: ''
      });
      await refreshAfterMutation(selectedItem.item_id);
      alert('Stock distributed successfully!');
    } catch (error: any) {
      alert(error.response?.data?.message || error.message || 'Failed to distribute stock. Please check available stock.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const addDistributionRow = () => {
    setDistributionData(prev => ({
      ...prev,
      distributions: [...prev.distributions, { stall_id: '', quantity: '' }]
    }));
  };

  const removeDistributionRow = (index: number) => {
    setDistributionData(prev => ({
      ...prev,
      distributions: prev.distributions.filter((_, i) => i !== index)
    }));
  };

  const updateDistribution = (index: number, field: 'stall_id' | 'quantity', value: string) => {
    setDistributionData(prev => ({
      ...prev,
      distributions: prev.distributions.map((dist, i) =>
        i === index ? { ...dist, [field]: value } : dist
      )
    }));
  };

  const handleEditDistSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingDist) return;

    const quantity = parseInt(editDistQty, 10);
    const stallId = parseInt(editDistStallId, 10);

    if (!Number.isInteger(quantity) || quantity <= 0) {
      alert('Quantity must be a whole number greater than 0');
      return;
    }

    if (!Number.isInteger(stallId)) {
      alert('Please select a stall');
      return;
    }

    setIsSubmitting(true);
    try {
      await dataApi.updateDistribution(editingDist.distribution_id, quantity, stallId);
      setShowEditDistModal(false);
      setEditingDist(null);
      await refreshAfterMutation(editingDist.item_id ?? expandedItemId ?? undefined);
      alert('Distribution updated successfully!');
    } catch (error: any) {
      alert(error.message || 'Failed to update distribution');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteDist = async (dist: any) => {
    const qty = Number(dist.quantity_allocated) || 0;
    const stall = dist.stall_name || 'stall';
    if (
      !window.confirm(
        `Delete distribution: return ${qty} unit(s) from ${stall} to Central Hub.\n\nThis cannot be undone. Continue?`
      )
    ) {
      return;
    }

    try {
      await dataApi.deleteDistribution(dist.distribution_id);
      await refreshAfterMutation(dist.item_id ?? expandedItemId ?? undefined);
      alert('Distribution deleted and stock returned to hub.');
    } catch (error: any) {
      alert(error.message || 'Failed to delete distribution');
    }
  };

  const handleDeleteStockAddition = async (addition: any) => {
    const qty = Number(addition.quantity_added) || 0;
    if (
      !window.confirm(
        `Delete stock addition of ${qty} unit(s). Total Received and hub stock will drop by ${qty}.\n\nThis cannot be undone. Continue?`
      )
    ) {
      return;
    }

    try {
      await dataApi.deleteStockAddition(addition.addition_id);
      await refreshAfterMutation(addition.item_id ?? expandedItemId ?? undefined);
      alert('Stock addition deleted successfully.');
    } catch (error: any) {
      alert(error.message || 'Failed to delete stock addition');
    }
  };

  const handleWithdrawFromDistSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!withdrawFromDist || !withdrawFromDistQty || isSubmitting) return;

    const quantityToWithdraw = parseInt(withdrawFromDistQty, 10);
    if (!Number.isInteger(quantityToWithdraw) || quantityToWithdraw <= 0) {
      alert('Withdrawal quantity must be a whole number greater than 0');
      return;
    }

    // Cap at unsold on this stall and this batch — sales do not shrink quantity_allocated.
    const stallSold =
      salesAggregates.byItemStall[`${withdrawFromDist.item_id}-${withdrawFromDist.stall_id}`] ?? 0;
    const sameStallBatches = itemDistributions.filter(
      (d: any) =>
        Number(d.item_id) === Number(withdrawFromDist.item_id) &&
        Number(d.stall_id) === Number(withdrawFromDist.stall_id)
    );
    const totalAllocated = sameStallBatches.reduce(
      (sum: number, d: any) => sum + (Number(d.quantity_allocated) || 0),
      0
    );
    const stallLeft = Math.max(0, totalAllocated - stallSold);
    const maxReturnable = Math.min(
      Number(withdrawFromDist.quantity_allocated) || 0,
      stallLeft
    );

    if (quantityToWithdraw > maxReturnable) {
      alert(
        `Cannot withdraw ${quantityToWithdraw} items. Only ${maxReturnable} unsold unit(s) can be returned from ${withdrawFromDist.stall_name}.`
      );
      return;
    }

    const stallAfter = Math.max(0, stallLeft - quantityToWithdraw);
    if (
      !window.confirm(
        `Return ${quantityToWithdraw} from ${withdrawFromDist.stall_name} to Central Hub. Stock available before withdrawal: ${stallLeft} → after: ${stallAfter}. Continue?`
      )
    ) {
      return;
    }

    setIsSubmitting(true);
    try {
      const result = await dataApi.withdrawFromDistribution(withdrawFromDist.distribution_id, quantityToWithdraw);
      const capturedItemId = withdrawFromDist.item_id ?? expandedItemId;
      const capturedStallName = withdrawFromDist.stall_name;
      setShowWithdrawFromDistModal(false);
      setWithdrawFromDist(null);
      setWithdrawFromDistQty('');
      await refreshAfterMutation(capturedItemId ?? undefined);

      // Show undo toast if we have a withdrawal_id to reverse
      if (result?.withdrawalId) {
        // Clear any existing timer
        if (undoTimerRef.current) clearTimeout(undoTimerRef.current);
        setUndoToast({
          withdrawalId: result.withdrawalId,
          qty: quantityToWithdraw,
          stallName: capturedStallName,
          itemId: capturedItemId,
        });
        // Auto-dismiss after 10 seconds
        undoTimerRef.current = setTimeout(() => setUndoToast(null), 10000);
      }
    } catch (error: any) {
      alert(error.message || 'Failed to withdraw from distribution');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleUndoWithdrawal = async () => {
    if (!undoToast) return;
    if (undoTimerRef.current) clearTimeout(undoTimerRef.current);
    setUndoToast(null);
    try {
      await dataApi.deleteStockWithdrawal(undoToast.withdrawalId);
      await refreshAfterMutation(undoToast.itemId);
    } catch (error: any) {
      alert(error.message || 'Failed to undo withdrawal');
    }
  };


  const handleAddStockSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedItem || !addStockQuantity || isSubmitting) return;

    const quantityToAdd = parseInt(addStockQuantity, 10);
    if (!Number.isInteger(quantityToAdd) || quantityToAdd <= 0) {
      alert('Quantity must be a whole number greater than 0');
      return;
    }

    setIsSubmitting(true);
    try {
      // Send the exact quantity typed by the user (delta). The server records
      // it in stock_additions and recomputes totals atomically, so stale
      // on-screen numbers can never shrink or inflate the addition.
      await dataApi.addStock(selectedItem.item_id, quantityToAdd);

      setShowAddStockModal(false);
      setAddStockQuantity('');
      await refreshAfterMutation(selectedItem.item_id);
      alert('Stock added successfully!');
    } catch (error: any) {
      console.error('Error adding stock:', error);
      alert(error.response?.data?.message || error.message || 'Failed to add stock');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleWithdrawSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedItem || !withdrawQuantity || !user || isSubmitting) return;

    const quantityToWithdraw = parseInt(withdrawQuantity, 10);
    if (!Number.isInteger(quantityToWithdraw) || quantityToWithdraw <= 0) {
      alert('Quantity must be greater than 0');
      return;
    }

    const comment = withdrawReason.trim();
    if (!comment) {
      alert('Please add a short comment so this withdrawal is easy to recall later (e.g. Personal use, Gift).');
      return;
    }

    // --- Validate available stock per source ---
    let confirmLine = '';
    if (withdrawSource === 'central') {
      const available = selectedItem.current_stock ?? 0;
      if (available <= 0) {
        alert('No stock available in Central Hub. There is nothing to withdraw.');
        return;
      }
      if (quantityToWithdraw > available) {
        alert(`Insufficient stock in Central Hub. Available: ${available}`);
        return;
      }
      const after = available - quantityToWithdraw;
      confirmLine =
        `Remove ${quantityToWithdraw} from Central Hub (${comment}). Stock available before withdrawal: ${available} → after: ${after}. Continue?`;
    } else {
      // Stall withdrawal — compute remaining using allocated - sold (same formula as table)
      const stallSold = salesAggregates.byItemStall[`${selectedItem.item_id}-${withdrawSource}`] ?? 0;
      const stallBatches = withdrawItemDistributions.filter(
        (d) => String(d.stall_id) === withdrawSource
      );
      const totalAllocated = stallBatches.reduce((sum, d) => sum + (d.quantity_allocated || 0), 0);
      const totalAvailable = Math.max(0, totalAllocated - stallSold);
      const stallName = stallBatches[0]?.stall_name ?? `Stall #${withdrawSource}`;

      if (!stallBatches.length) {
        alert('Selected stall distribution not found. Please refresh and try again.');
        return;
      }
      if (totalAvailable <= 0) {
        alert(`No stock left at ${stallName}. There is nothing to withdraw.`);
        return;
      }
      if (quantityToWithdraw > totalAvailable) {
        alert(`Insufficient stock at ${stallName}. Available: ${totalAvailable}`);
        return;
      }
      const after = totalAvailable - quantityToWithdraw;
      confirmLine =
        `Return ${quantityToWithdraw} from ${stallName} to Central Hub (${comment}). Stock available before withdrawal: ${totalAvailable} → after: ${after}. Continue?`;
    }

    if (!window.confirm(confirmLine)) {
      return;
    }

    setIsSubmitting(true);
    try {
      const itemId = selectedItem.item_id;
      const itemName = selectedItem.item_name;
      const sourceAtSubmit = withdrawSource;
      let successMessage = '';
      if (sourceAtSubmit === 'central') {
        // Central Hub withdrawal
        await dataApi.createWithdrawal({
          item_id: itemId,
          quantity_withdrawn: quantityToWithdraw,
          reason: comment,
          withdrawn_by: user.user_id,
          notes: `Owner withdrawal: ${comment}`
        });
        successMessage = `✅ Successfully withdrew ${quantityToWithdraw} ${itemName}(s) from central hub.`;

        setItems((prev) =>
          prev.map((item) =>
            item.item_id === itemId
              ? {
                  ...item,
                  current_stock: Math.max(0, (item.current_stock || 0) - quantityToWithdraw)
                }
              : item
          )
        );
        setSelectedItem((prev) =>
          prev && prev.item_id === itemId
            ? {
                ...prev,
                current_stock: Math.max(0, (prev.current_stock || 0) - quantityToWithdraw)
              }
            : prev
        );
      } else {
        // Stall withdrawal — one history row even if multiple distribution batches
        const stallBatches = withdrawItemDistributions
          .filter((d) => String(d.stall_id) === sourceAtSubmit)
          .sort((a, b) => new Date(a.date_distributed).getTime() - new Date(b.date_distributed).getTime());
        const stallName = stallBatches[0]?.stall_name ?? `Stall #${sourceAtSubmit}`;

        const withdrawResult = await dataApi.withdrawFromStall({
          item_id: itemId,
          stall_id: Number(sourceAtSubmit),
          quantity: quantityToWithdraw,
          reason: comment,
          notes: `Returned to central hub: ${comment}`
        });
        successMessage = `✅ Successfully withdrew ${quantityToWithdraw} ${itemName}(s) from ${stallName} back to central hub.`;

        const stallId = Number(sourceAtSubmit);
        const hubAfter =
          withdrawResult?.newCentralStock != null
            ? Number(withdrawResult.newCentralStock)
            : (selectedItem.current_stock || 0) + quantityToWithdraw;

        setItems((prev) =>
          prev.map((item) =>
            item.item_id === itemId
              ? {
                  ...item,
                  current_stock: hubAfter,
                  total_allocated: Math.max(0, (item.total_allocated || 0) - quantityToWithdraw)
                }
              : item
          )
        );
        setSelectedItem((prev) =>
          prev && prev.item_id === itemId
            ? {
                ...prev,
                current_stock: hubAfter,
                total_allocated: Math.max(0, (prev.total_allocated || 0) - quantityToWithdraw)
              }
            : prev
        );
        setStallAllocMap((prev) => {
          const next = new Map(prev);
          const allocs = [...(next.get(itemId) || [])];
          let left = quantityToWithdraw;
          next.set(
            itemId,
            allocs.map((a: any) => {
              if (left <= 0 || Number(a.stall_id) !== stallId) return a;
              const take = Math.min(left, a.quantity_allocated || 0);
              left -= take;
              return { ...a, quantity_allocated: (a.quantity_allocated || 0) - take };
            })
          );
          return next;
        });
      }

      setShowWithdrawModal(false);
      setWithdrawQuantity('');
      setWithdrawReason('');
      setWithdrawSource('central');
      setWithdrawItemDistributions([]);

      // Refresh first so the table already shows the new hub before the alert.
      await refreshAfterMutation(itemId);
      alert(successMessage);
    } catch (error: any) {
      console.error('Error withdrawing stock:', error);
      alert(error.message || 'Failed to withdraw stock. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEditSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedItem || isSubmitting) return;

    setIsSubmitting(true);
    try {
      const initialStock = parseInt(editFormData.initial_stock);
      const totalAdded = parseInt(editFormData.total_added);
      const currentStock = parseInt(editFormData.current_stock);

      if (Number.isNaN(initialStock) || initialStock < 0) {
        alert('Initial stock must be zero or greater.');
        setIsSubmitting(false);
        return;
      }

      if (Number.isNaN(totalAdded) || totalAdded < 0) {
        alert('Total added must be zero or greater.');
        setIsSubmitting(false);
        return;
      }

      const unitPrice = parseFloat(editFormData.unit_price);

      if (Number.isNaN(unitPrice) || unitPrice <= 0) {
        alert('Unit price must be greater than zero.');
        setIsSubmitting(false);
        return;
      }

      await dataApi.updateItem(selectedItem.item_id, {
        item_name: editFormData.item_name,
        category: editFormData.category,
        unit_price: unitPrice,
        buying_price: parseFloat(editFormData.buying_price) || 0,
        initial_stock: initialStock,
        total_added: totalAdded,
        current_stock: currentStock
      });

      setShowEditModal(false);
      await refreshAfterMutation(selectedItem.item_id);
      alert('Item updated successfully!');
    } catch (error: any) {
      alert(error.response?.data?.message || 'Failed to update item');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteItem = async (itemId: number, itemName: string) => {
    const sold = getItemsSold(itemId, itemName);
    const message = sold > 0
      ? `Delete item "${itemName}" from inventory (${sold} sale(s) kept in history). This cannot be undone. Continue?`
      : `Delete item "${itemName}" from inventory. This cannot be undone. Continue?`;

    const confirmed = window.confirm(message);
    if (!confirmed) return;

    try {
      await dataApi.deleteItem(itemId);
      await fetchItems();
      // Clear selectedItem if it was the deleted item
      if (selectedItem?.item_id === itemId) {
        setSelectedItem(null);
      }
      alert('Item removed from inventory successfully!');
    } catch (error: any) {
      alert(error.response?.data?.message || error.message || 'Failed to delete item');
    }
  };

  const filteredItems = items.filter(item => {
    const matchesSearch = item.item_name.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = !selectedCategory || item.category === selectedCategory;
    const matchesLowStock = !showLowStock || item.current_stock <= 5;

    return matchesSearch && matchesCategory && matchesLowStock;
  });

  const getItemsSold = (itemId: number, fallbackName: string, stallOnly: boolean = false) => {
    const numericItemId = Number(itemId);

    // Non-admin: filter by recorded user from loaded sales list
    if (user?.role !== 'admin' && user?.user_id) {
      return salesData
        .filter((sale) => {
          const matchesItem = sale.item_id != null
            ? Number(sale.item_id) === numericItemId
            : sale.item_name === fallbackName;
          if (!matchesItem) return false;
          if (stallOnly && (sale.stall_id === null || sale.stall_id === undefined)) return false;
          return sale.recorded_by === user.user_id;
        })
        .reduce((total, sale) => total + (sale.quantity_sold || 0), 0);
    }

    // Admin: use full-database aggregates (paginated fetch avoids 1000-row Supabase cap)
    if (stallOnly) {
      return Object.entries(salesAggregates.byItemStall)
        .filter(([key]) => key.startsWith(`${numericItemId}-`) && !key.endsWith('-null'))
        .reduce((sum, [, qty]) => sum + qty, 0);
    }

    return salesAggregates.byItem[numericItemId] ?? 0;
  };

  const getStallSold = (itemId: number, stallId: number) =>
    salesAggregates.byItemStall[`${Number(itemId)}-${stallId}`] ?? 0;

  /** Scale per-stall remainings so they sum to allocated − sold (handles pre-tracking dist rows). */
  const reconcileStallRemainings = (
    entries: Array<{ name: string; rawRemaining: number }>,
    targetTotal: number
  ): Array<{ name: string; remaining: number }> => {
    const totalRaw = entries.reduce((t, e) => t + e.rawRemaining, 0);
    if (targetTotal <= 0 || totalRaw <= 0) {
      return entries.map((e) => ({ name: e.name, remaining: 0 }));
    }
    if (totalRaw === targetTotal) {
      return entries.map((e) => ({ name: e.name, remaining: e.rawRemaining }));
    }

    const positive = entries.filter((e) => e.rawRemaining > 0);
    let assigned = 0;
    return entries.map((e) => {
      if (e.rawRemaining <= 0) return { name: e.name, remaining: 0 };
      const posIndex = positive.findIndex((p) => p.name === e.name);
      if (posIndex === positive.length - 1) {
        return { name: e.name, remaining: Math.max(0, targetTotal - assigned) };
      }
      const scaled = Math.floor((e.rawRemaining / totalRaw) * targetTotal);
      assigned += scaled;
      return { name: e.name, remaining: scaled };
    });
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-KE', {
      style: 'currency',
      currency: 'KES'
    }).format(amount);
  };

  const selectedItemSold = selectedItem ? getItemsSold(selectedItem.item_id, selectedItem.item_name) : 0;
  // Only stall sales reduce distributed stock; central sales already reduce current_stock
  const selectedItemStallSold = selectedItem ? getItemsSold(selectedItem.item_id, selectedItem.item_name, true) : 0;
  const selectedItemDistributed = selectedItem
    ? Math.max(0, (selectedItem.total_allocated || 0) - selectedItemStallSold)
    : 0;
  const selectedItemAvailable = selectedItem ? Math.max(0, selectedItem.current_stock || 0) : 0;
  // Matches the "Total received" figure on the expanded item row so the modal
  // and the row never show two different totals for the same item.
  const selectedItemReceived = selectedItem
    ? (selectedItem.initial_stock || 0) + (selectedItem.total_added || 0)
    : 0;
  const quantityToAddPreview = addStockQuantity ? parseInt(addStockQuantity, 10) || 0 : 0;
  const previewAvailableAfterAdd = selectedItemAvailable + quantityToAddPreview;
  const previewTotalReceived = selectedItemReceived + quantityToAddPreview;
  const totalQuantityPendingDistribution = distributionData.distributions.reduce(
    (sum, dist) => sum + (parseInt(dist.quantity) || 0),
    0
  );
  const remainingAfterPendingDistribution = Math.max(
    0,
    selectedItemAvailable - totalQuantityPendingDistribution
  );


  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (user?.role !== 'admin') {
    return (
      <div className="space-y-4 sm:space-y-6">
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4">
          <div className="flex-1 min-w-0">
            <h1 className="text-xl sm:text-2xl font-bold" style={{ color: 'var(--primary-800)' }}>Inventory</h1>
            <p style={{ color: 'var(--neutral-600)' }}>Manage your available items</p>
          </div>
        </div>
        <UserSaleItems />
      </div>
    );
  }

  return (
    <div className="space-y-4 sm:space-y-6">
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4">
        <div className="flex-1 min-w-0">
          <h1 className="text-xl sm:text-2xl font-bold" style={{ color: 'var(--primary-800)' }}>Inventory Management</h1>
          <p className="text-sm sm:text-base" style={{ color: 'var(--neutral-600)' }}>Manage your inventory items and stock</p>
        </div>
        {user?.role === 'admin' && (
          <button
            onClick={() => navigate('/add-item')}
            className="btn-primary text-sm sm:text-base px-3 sm:px-4 py-2 whitespace-nowrap self-start sm:self-auto"
          >
            Add New Item
          </button>
        )}
      </div>

      {loadWarning && (
        <div className="flex flex-col sm:flex-row sm:items-center gap-3 rounded-lg border border-amber-300 bg-amber-50 p-4">
          <p className="flex-1 text-sm text-amber-900">{loadWarning}</p>
          <button
            type="button"
            onClick={() => { fetchItems(); fetchSales(); fetchStalls(); }}
            className="self-start rounded-md border border-amber-400 bg-white px-3 py-1.5 text-sm font-medium text-amber-900 hover:bg-amber-100"
          >
            Retry
          </button>
        </div>
      )}

      {/* Filters */}
      <div className="bg-white p-4 rounded-lg shadow">
        <div className={`grid grid-cols-1 ${user?.role === 'admin' ? 'md:grid-cols-5' : 'md:grid-cols-4'} gap-4`}>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Search Items</label>
            <div className="flex gap-2">
              <input
                type="text"
                placeholder="Search by name..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
            <select
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">All Categories</option>
              {categories.map(category => (
                <option key={category} value={category}>{category}</option>
              ))}
            </select>
          </div>

          {user?.role === 'admin' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Stall</label>
              <select
                value={selectedStall}
                onChange={(e) => setSelectedStall(e.target.value === '' ? '' : parseInt(e.target.value))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">All Stalls</option>
                {stalls.map(stall => (
                  <option key={stall.stall_id} value={stall.stall_id}>{stall.stall_name}</option>
                ))}
              </select>
            </div>
          )}

          <div className="flex items-center">
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={showLowStock}
                onChange={(e) => setShowLowStock(e.target.checked)}
                className="mr-2"
              />
              <span className="text-sm text-gray-700">Show Low Stock Only</span>
            </label>
          </div>

          <div className="flex items-end">
            <span className="text-sm text-gray-600">
              Showing {filteredItems.length} of {items.length} items
            </span>
          </div>
        </div>
      </div>

      {/* Items Table */}
      <div className="bg-white rounded-lg shadow-lg overflow-hidden border border-gray-200">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider w-10">
                  #
                </th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                  Item
                </th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                  Category
                </th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                  Stock
                </th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                  Items Sold
                </th>
                {user?.role === 'admin' && (
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                    Buying Price
                  </th>
                )}
                {user?.role === 'admin' && (
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                    Value
                  </th>
                )}
                {user?.role === 'admin' && (
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                    Actions
                  </th>
                )}
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-100">
              {filteredItems.map((item, index) => {
                const totalSoldForItem = getItemsSold(item.item_id, item.item_name);
                // Only STALL sales come out of distributed stock; central hub
                // sales are already deducted from current_stock.
                const stallSoldForItem = getItemsSold(item.item_id, item.item_name, true);
                const distributedLive = Math.max(0, (item.total_allocated || 0) - stallSoldForItem);
                const centralStock = Math.max(0, item.current_stock || 0);
                // Total Received = initial stock + all items added (total inventory received into system)
                const totalReceived = (item.initial_stock || 0) + (item.total_added || 0);
                const managedTotal = totalReceived;
                const centralPercent = managedTotal > 0 ? Math.round((centralStock / managedTotal) * 100) : 0;
                const distributedPercent = managedTotal > 0 ? Math.round(((item.total_allocated || 0) / managedTotal) * 100) : 0;
                const isExpanded = expandedItemId === item.item_id;
                const columnSpan = user?.role === 'admin' ? 8 : 5;

                return (
                  <React.Fragment key={item.item_id}>
                    <tr
                      onClick={() => toggleExpand(item.item_id)}
                      className={`hover:bg-blue-50/40 cursor-pointer transition-all group ${isExpanded ? 'bg-blue-50/50 shadow-inner' : ''}`}
                    >
                      <td className="px-4 py-4 whitespace-nowrap text-center">
                        <button
                          type="button"
                          className={`inline-flex h-8 w-8 items-center justify-center rounded-lg text-sm font-bold transition-all shadow-sm ${isExpanded ? 'bg-blue-600 text-white shadow-md' : 'bg-gray-100 text-gray-600 group-hover:bg-blue-200 group-hover:text-blue-700'}`}
                        >
                          {index + 1}
                        </button>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div>
                          <div className="text-sm font-bold text-gray-900">{item.item_name}</div>
                          {user?.role === 'admin' ? (() => {
                            const allocs = stallAllocMap.get(item.item_id) || [];
                            // Group distribution batches by stall_id (not name — avoids mismatches)
                            const byStallId = new Map<number, number>();
                            allocs.forEach((a: { stall_id?: number; quantity_allocated: number }) => {
                              const sid = Number(a.stall_id);
                              if (!sid) return;
                              byStallId.set(sid, (byStallId.get(sid) || 0) + (a.quantity_allocated || 0));
                            });

                            const rawStallEntries = stalls.map(stall => ({
                              name: stall.stall_name,
                              rawRemaining: Math.max(
                                0,
                                (byStallId.get(stall.stall_id) || 0) - getStallSold(item.item_id, stall.stall_id)
                              )
                            }));

                            const stallEntries = reconcileStallRemainings(rawStallEntries, distributedLive);
                            const totalInSystem = centralStock + distributedLive;
                            
                            return (
                              <>
                                <div className="text-[11px] font-medium text-gray-400 uppercase tracking-tight">
                                  TOTAL IN SYSTEM: {totalInSystem}
                                </div>
                                <div className="flex flex-col mt-0.5 space-y-0">
                                  {stallEntries.map((s, i) => (
                                    <span key={i} className="text-[10px] font-semibold text-blue-600 whitespace-nowrap">
                                      {s.name} - {s.remaining}
                                    </span>
                                  ))}
                                </div>
                              </>
                            );
                          })() : (
                            <div className="text-[11px] font-medium text-gray-400 uppercase tracking-tight">
                              TOTAL IN SYSTEM: {centralStock + distributedLive}
                            </div>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {item.category}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap align-top">
                        {user?.role === 'admin' ? (
                          <div className="space-y-2">
                            <div className="flex justify-between text-xs text-gray-500">
                              <span>Available (central)</span>
                              <span className="text-sm font-semibold text-gray-900">{centralStock}</span>
                            </div>
                            <div className="flex justify-between text-xs text-blue-600">
                              <span>Out at stalls</span>
                              <span className="text-sm font-semibold">{distributedLive}</span>
                            </div>
                            <div className="flex justify-between text-xs text-purple-600">
                              <span>Sold</span>
                              <span className="text-sm font-semibold">{totalSoldForItem}</span>
                            </div>
                            <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                              <div
                                className="h-full bg-indigo-500"
                                style={{ width: `${centralPercent}%` }}
                              ></div>
                              <div
                                className="h-full bg-blue-400"
                                style={{ width: `${distributedPercent}%` }}
                              ></div>
                            </div>
                            <div className="flex justify-between text-[11px] text-gray-400">
                              <span>Total received</span>
                              <span>{totalReceived}</span>
                            </div>
                          </div>
                        ) : (
                          <>
                            <div className="text-sm text-gray-900">{item.current_stock}</div>
                            <div className="text-xs text-gray-500">
                              Initial: {item.initial_stock} | New items added: {item.total_added}
                            </div>
                          </>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {totalSoldForItem}
                      </td>
                      {user?.role === 'admin' && (
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {formatCurrency(item.buying_price || 0)}
                        </td>
                      )}
                      {user?.role === 'admin' && (
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {formatCurrency((centralStock + distributedLive) * (item.buying_price || 0))}
                        </td>
                      )}
                      {user?.role === 'admin' && (
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                          <div className="flex items-center space-x-3">
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                setSelectedItem(item);
                                setDistributionData({
                                  item_id: item.item_id,
                                  distributions: [],
                                  notes: ''
                                });
                                setShowDistributeModal(true);
                              }}
                              className="text-blue-600 hover:text-blue-900"
                            >
                              Distribute
                            </button>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                setSelectedItem(item);
                                setAddStockQuantity('');
                                setShowAddStockModal(true);
                              }}
                              className="text-green-600 hover:text-green-900"
                            >
                              Add Stock
                            </button>
                            <button
                              onClick={async (e) => {
                                e.stopPropagation();
                                setSelectedItem(item);
                                setWithdrawQuantity('');
                                setWithdrawReason('');
                                setWithdrawSource('central');
                                // Pre-load distributions so the dropdown is ready
                                try {
                                  const res = await dataApi.getDistributions(item.item_id);
                                  setWithdrawItemDistributions(res.distributions || []);
                                } catch {
                                  setWithdrawItemDistributions([]);
                                }
                                setShowWithdrawModal(true);
                              }}
                              className="text-orange-600 hover:text-orange-900"
                              title="Owner withdrawal"
                            >
                              Withdraw
                            </button>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                setSelectedItem(item);
                                setEditFormData({
                                  item_name: item.item_name,
                                  category: item.category,
                                  unit_price: item.unit_price.toString(),
                                  buying_price: (item.buying_price || 0).toString(),
                                  initial_stock: item.initial_stock.toString(),
                                  total_added: item.total_added.toString(),
                                  current_stock: item.current_stock.toString()
                                });
                                setShowEditModal(true);
                              }}
                              className="text-purple-600 hover:text-purple-900"
                            >
                              Edit
                            </button>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDeleteItem(item.item_id, item.item_name);
                              }}
                              className="text-red-600 hover:text-red-900"
                              title="Remove item"
                            >
                              🗑️
                            </button>
                          </div>
                        </td>
                      )}
                    </tr>
                    {isExpanded && (
                      <tr className="bg-gray-50">
                        <td className="px-6 pb-6 pt-0" colSpan={columnSpan}>
                          <div className="pt-4">
                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                              <div className="rounded-lg border border-emerald-100 bg-emerald-50 p-4">
                                <div className="text-xs font-semibold uppercase text-emerald-700">
                                  Available to distribute
                                </div>
                                <div className="mt-2 text-2xl font-bold text-emerald-900">
                                  {centralStock}
                                </div>
                                <p className="mt-1 text-xs text-emerald-700">
                                  Ready in central store
                                </p>
                              </div>
                              <div className="rounded-lg border border-blue-100 bg-blue-50 p-4">
                                <div className="text-xs font-semibold uppercase text-blue-700">
                                  At stalls (unsold)
                                </div>
                                <div className="mt-2 text-2xl font-bold text-blue-900">
                                  {distributedLive}
                                </div>
                                <p className="mt-1 text-xs text-blue-700">
                                  Stock currently with stalls
                                </p>
                              </div>
                              <div className="rounded-lg border border-purple-100 bg-purple-50 p-4">
                                <div className="text-xs font-semibold uppercase text-purple-700">
                                  Sold
                                </div>
                                <div className="mt-2 text-2xl font-bold text-purple-900">
                                  {totalSoldForItem}
                                </div>
                                <p className="mt-1 text-xs text-purple-700">
                                  Recorded sales so far
                                </p>
                              </div>
                              <div className="rounded-lg border border-indigo-100 bg-indigo-50 p-4">
                                <div className="text-xs font-semibold uppercase text-indigo-700">
                                  Allocated So Far
                                </div>
                                <div className="mt-2 text-2xl font-bold text-indigo-900">
                                  {distributedLive + stallSoldForItem}
                                </div>
                                <p className="mt-1 text-xs text-indigo-700">
                                  At stalls + sold at stalls
                                </p>
                              </div>
                            </div>

                            {centralStock === 0 && (
                              <div className="mt-4 rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-700">
                                No stock left in the central store. Add new stock before distributing again.
                              </div>
                            )}

                            <div className="mt-6 rounded-lg border border-gray-200 bg-white p-4">
                              <h4 className="text-sm font-semibold text-gray-900">Stock flow</h4>
                              <p className="mt-1 text-xs text-gray-500">
                                {(item.initial_stock || 0)} initial + {(item.total_added || 0)} added ={' '}
                                {totalReceived} total inventory.{' '}
                                {distributedLive + totalSoldForItem} allocated so far ({distributedLive} at stalls, {totalSoldForItem} sold), {centralStock} available in central.
                              </p>
                              {expandedItemId === item.item_id && itemStockWithdrawals.length > 0 && (
                                <p className="mt-2 text-xs text-amber-800 bg-amber-50 border border-amber-100 rounded-md px-3 py-2">
                                  Withdrawals recorded:{' '}
                                  {itemStockWithdrawals
                                    .filter(w => w.stall_id == null)
                                    .reduce((sum, w) => sum + (w.quantity_withdrawn || 0), 0)}{' '}
                                  from <strong>Central Hub</strong>
                                  {itemStockWithdrawals.some(w => w.stall_id != null) && (
                                    <>
                                      {' · '}
                                      {itemStockWithdrawals
                                        .filter(w => w.stall_id != null)
                                        .reduce((sum, w) => sum + (w.quantity_withdrawn || 0), 0)}{' '}
                                      from <strong>stalls</strong> (see history for which stall)
                                    </>
                                  )}
                                  . Only central-hub withdrawals reduce &quot;Available to distribute&quot;.
                                </p>
                              )}
                              <div className="mt-3 grid grid-cols-1 sm:grid-cols-3 gap-3 text-sm">
                                <div className="rounded border border-gray-100 bg-gray-50 p-3">
                                  <div className="text-xs uppercase text-gray-500">Initial stock</div>
                                  <div className="mt-1 text-lg font-semibold text-gray-900">{item.initial_stock}</div>
                                </div>
                                <div className="rounded border border-gray-100 bg-gray-50 p-3">
                                  <div className="text-xs uppercase text-gray-500">New items added</div>
                                  <div className="mt-1 text-lg font-semibold text-gray-900">{item.total_added}</div>
                                </div>
                                <div className="rounded border border-gray-100 bg-gray-50 p-3">
                                  <div className="text-xs uppercase text-gray-500">Total Inventory</div>
                                  <div className="mt-1 text-lg font-semibold text-gray-900">{totalReceived}</div>
                                  <div className="mt-1 text-xs text-gray-400">Initial + Added</div>
                                </div>
                              </div>
                            </div>

                            {user?.role === 'admin' && (
                              <div className="mt-6 rounded-lg border border-gray-200 bg-white p-4">
                                <div className="flex justify-between items-center mb-4">
                                  <h4 className="text-lg font-bold text-gray-900">Distribution History</h4>
                                  {isRefreshingDist && <span className="text-xs text-blue-500 animate-pulse">Refreshing...</span>}
                                </div>

                                {itemDistributions.length > 0 ? (
                                  <div className="overflow-x-auto">
                                    <p className="text-xs text-gray-500 mb-2">
                                      Hub stock when this send happened.
                                    </p>
                                    <table className="min-w-full divide-y divide-gray-200">
                                      <thead className="bg-[#f0f9ff]">
                                        <tr>
                                          <th className="px-3 py-3 text-left text-[11px] font-bold text-blue-900 uppercase tracking-wider">Date</th>
                                          <th className="px-3 py-3 text-left text-[11px] font-bold text-blue-900 uppercase tracking-wider">Destination Stall</th>
                                          <th className="px-3 py-3 text-center text-[11px] font-bold text-blue-900 uppercase tracking-wider">Stock available before distribution</th>
                                          <th className="px-3 py-3 text-center text-[11px] font-bold text-blue-900 uppercase tracking-wider">Sent</th>
                                          <th className="px-3 py-3 text-center text-[11px] font-bold text-blue-900 uppercase tracking-wider">Stock available after distribution</th>
                                          <th className="px-4 py-3 text-right text-[11px] font-bold text-blue-900 uppercase tracking-wider">Options</th>
                                        </tr>
                                      </thead>
                                      <tbody className="divide-y divide-gray-200">
                                        {itemDistributions.map((dist) => (
                                          <tr key={dist.distribution_id} className="text-sm hover:bg-blue-50/30 transition-colors">
                                            <td className="px-3 py-3 whitespace-nowrap text-gray-700 font-semibold">
                                              {new Date(dist.date_distributed).toLocaleDateString(undefined, { day: '2-digit', month: '2-digit', year: 'numeric' })}
                                            </td>
                                            <td className="px-3 py-3 whitespace-nowrap font-bold text-indigo-900">
                                              {dist.stall_name}
                                            </td>
                                            <td className="px-3 py-3 whitespace-nowrap text-center font-semibold text-gray-800">
                                              {formatStockBalance(dist.stock_before)}
                                            </td>
                                            <td className="px-3 py-3 whitespace-nowrap text-center">
                                              <span className="inline-flex items-center justify-center px-2.5 py-1 rounded-full bg-blue-600 text-white text-xs font-black">
                                                −{dist.quantity_allocated}
                                              </span>
                                            </td>
                                            <td className="px-3 py-3 whitespace-nowrap text-center font-semibold text-gray-800">
                                              {formatStockBalance(dist.stock_after)}
                                            </td>
                                            <td className="px-4 py-3 whitespace-nowrap text-right font-bold">
                                              <button
                                                onClick={(e) => {
                                                  e.stopPropagation();
                                                  setEditingDist(dist);
                                                  setEditDistQty(dist.quantity_allocated.toString());
                                                  setEditDistStallId(dist.stall_id.toString());
                                                  setShowEditDistModal(true);
                                                }}
                                                className="bg-blue-50 text-blue-700 px-3 py-1 rounded-md hover:bg-blue-600 hover:text-white transition-all mr-2 border border-blue-200"
                                              >
                                                Edit
                                              </button>
                                              <button
                                                onClick={(e) => {
                                                  e.stopPropagation();
                                                  handleDeleteDist(dist);
                                                }}
                                                className="bg-red-50 text-red-700 px-3 py-1 rounded-md hover:bg-red-600 hover:text-white transition-all border border-red-200"
                                              >
                                                Delete
                                              </button>
                                            </td>
                                          </tr>
                                        ))}
                                      </tbody>
                                    </table>
                                  </div>
                                ) : (
                                  <p className="text-sm text-gray-500 italic text-center py-4 bg-gray-50 rounded-lg">No distribution records found for this item.</p>
                                )}
                              </div>
                            )}

                            {user?.role === 'admin' && (
                              <div className="mt-6 rounded-lg border border-gray-200 bg-white p-4">
                                <div className="flex justify-between items-center mb-4">
                                  <h4 className="text-lg font-bold text-gray-900">Stock Additions History</h4>
                                  {isRefreshingAdditions && <span className="text-xs text-blue-500 animate-pulse">Refreshing...</span>}
                                </div>

                                <div className="overflow-x-auto">
                                  <p className="text-xs text-gray-500 mb-2">
                                    Hub stock when this addition happened.
                                  </p>
                                  <table className="min-w-full divide-y divide-gray-200">
                                    <thead className="bg-[#f0f9ff]">
                                      <tr>
                                        <th className="px-3 py-3 text-left text-[11px] font-bold text-blue-900 uppercase tracking-wider">Date</th>
                                        <th className="px-3 py-3 text-center text-[11px] font-bold text-blue-900 uppercase tracking-wider">Stock available before addition</th>
                                        <th className="px-3 py-3 text-center text-[11px] font-bold text-blue-900 uppercase tracking-wider">Added</th>
                                        <th className="px-3 py-3 text-center text-[11px] font-bold text-blue-900 uppercase tracking-wider">Stock available after addition</th>
                                        <th className="px-4 py-3 text-right text-[11px] font-bold text-blue-900 uppercase tracking-wider">Options</th>
                                      </tr>
                                    </thead>
                                    <tbody className="divide-y divide-gray-200">
                                      {itemStockAdditions && itemStockAdditions.length > 0 ? (
                                        itemStockAdditions.map((addition) => (
                                          <tr key={addition.addition_id} className="text-sm hover:bg-green-50/30 transition-colors">
                                            <td className="px-3 py-3 whitespace-nowrap text-gray-700 font-semibold">
                                              {new Date(addition.date_added).toLocaleDateString(undefined, { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                                            </td>
                                            <td className="px-3 py-3 whitespace-nowrap text-center font-semibold text-gray-800">
                                              {formatStockBalance(addition.stock_before)}
                                            </td>
                                            <td className="px-3 py-3 whitespace-nowrap text-center">
                                              <span className="inline-flex items-center justify-center px-2.5 py-1 rounded-full bg-green-600 text-white text-xs font-black">
                                                +{addition.quantity_added}
                                              </span>
                                            </td>
                                            <td className="px-3 py-3 whitespace-nowrap text-center font-semibold text-gray-800">
                                              {formatStockBalance(addition.stock_after)}
                                            </td>
                                            <td className="px-4 py-3 whitespace-nowrap text-right font-bold">
                                              <button
                                                onClick={(e) => {
                                                  e.stopPropagation();
                                                  handleDeleteStockAddition(addition);
                                                }}
                                                className="bg-red-50 text-red-700 px-3 py-1 rounded-md hover:bg-red-600 hover:text-white transition-all border border-red-200"
                                              >
                                                Delete
                                              </button>
                                            </td>
                                          </tr>
                                        ))
                                      ) : (
                                        <tr className="text-sm text-gray-500 italic">
                                          <td colSpan={5} className="px-3 py-3 text-center bg-gray-50">
                                            No stock additions recorded yet
                                          </td>
                                        </tr>
                                      )}
                                    </tbody>
                                  </table>
                                </div>
                              </div>
                            )}

                            {user?.role === 'admin' && (
                              <div className="mt-6 rounded-lg border border-gray-200 bg-white p-4">
                                <div className="flex justify-between items-center mb-4">
                                  <h4 className="text-lg font-bold text-gray-900">Stock Withdrawals History</h4>
                                  {isRefreshingWithdrawals && <span className="text-xs text-blue-500 animate-pulse">Refreshing...</span>}
                                </div>

                                <div className="overflow-x-auto">
                                  <p className="text-xs text-gray-500 mb-2">
                                    Stock at that location (hub or stall) when this withdrawal happened.
                                  </p>
                                  <table className="min-w-full divide-y divide-gray-200">
                                    <thead className="bg-[#fff3cd]">
                                      <tr>
                                        <th className="px-3 py-3 text-left text-[11px] font-bold text-amber-900 uppercase tracking-wider">Date</th>
                                        <th className="px-3 py-3 text-left text-[11px] font-bold text-amber-900 uppercase tracking-wider">Withdrawn From</th>
                                        <th className="px-3 py-3 text-center text-[11px] font-bold text-amber-900 uppercase tracking-wider">Stock available before withdrawal</th>
                                        <th className="px-3 py-3 text-center text-[11px] font-bold text-amber-900 uppercase tracking-wider">Change</th>
                                        <th className="px-3 py-3 text-center text-[11px] font-bold text-amber-900 uppercase tracking-wider">Stock available after withdrawal</th>
                                        <th className="px-3 py-3 text-left text-[11px] font-bold text-amber-900 uppercase tracking-wider">Reason / Effect</th>
                                        <th className="px-4 py-3 text-right text-[11px] font-bold text-amber-900 uppercase tracking-wider">Options</th>
                                      </tr>
                                    </thead>
                                    <tbody className="divide-y divide-gray-200">
                                      {itemStockWithdrawals && itemStockWithdrawals.length > 0 ? (
                                        itemStockWithdrawals.map((withdrawal) => (
                                          <tr key={withdrawal.withdrawal_id} className="text-sm hover:bg-amber-50/30 transition-colors">
                                            <td className="px-3 py-3 whitespace-nowrap text-gray-700 font-semibold">
                                              {new Date(withdrawal.date_withdrawn).toLocaleDateString(undefined, { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                                            </td>
                                            <td className="px-3 py-3 whitespace-nowrap">
                                              <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-bold border ${withdrawalSourceBadgeClass(withdrawal)}`}>
                                                {formatWithdrawalSource(withdrawal)}
                                              </span>
                                            </td>
                                            <td className="px-3 py-3 whitespace-nowrap text-center font-semibold text-gray-800">
                                              {formatStockBalance(withdrawal.stock_before)}
                                            </td>
                                            <td className="px-3 py-3 whitespace-nowrap text-center">
                                              <span className="inline-flex items-center justify-center px-2.5 py-1 rounded-full bg-amber-600 text-white text-xs font-black">
                                                −{withdrawal.quantity_withdrawn}
                                              </span>
                                            </td>
                                            <td className="px-3 py-3 whitespace-nowrap text-center font-semibold text-gray-800">
                                              {formatStockBalance(withdrawal.stock_after)}
                                            </td>
                                            <td className="px-3 py-3 text-gray-600 text-xs">
                                              <div className="font-medium text-gray-800">{withdrawal.reason || '—'}</div>
                                              {withdrawal.notes && (
                                                <div className="text-gray-600 mt-0.5 italic">{withdrawal.notes}</div>
                                              )}
                                              <div className="text-gray-500 mt-0.5">{formatWithdrawalEffect(withdrawal)}</div>
                                            </td>
                                            <td className="px-4 py-3 whitespace-nowrap text-right font-bold">
                                              <button
                                                onClick={(e) => {
                                                  e.stopPropagation();
                                                  const source = formatWithdrawalSource(withdrawal);
                                                  const qty = withdrawal.quantity_withdrawn;
                                                  if (
                                                    window.confirm(
                                                      `Delete withdrawal of ${qty} unit(s) from ${source}${withdrawal.reason ? ` (${withdrawal.reason})` : ''}. Stock will be restored. Continue?`
                                                    )
                                                  ) {
                                                    dataApi.deleteStockWithdrawal(withdrawal.withdrawal_id)
                                                      .then(() => {
                                                        refreshAfterMutation(withdrawal.item_id ?? expandedItemId ?? undefined);
                                                        alert('Withdrawal deleted successfully.');
                                                      })
                                                      .catch((error) => {
                                                        console.error('Error deleting withdrawal:', error);
                                                        alert('Failed to delete withdrawal.');
                                                      });
                                                  }
                                                }}
                                                className="bg-red-50 text-red-700 px-3 py-1 rounded-md hover:bg-red-600 hover:text-white transition-all border border-red-200"
                                              >
                                                Delete
                                              </button>
                                            </td>
                                          </tr>
                                        ))
                                      ) : (
                                        <tr className="text-sm text-gray-500 italic">
                                          <td colSpan={7} className="px-3 py-3 text-center bg-gray-50">
                                            No stock withdrawals recorded
                                          </td>
                                        </tr>
                                      )}
                                    </tbody>
                                  </table>
                                </div>
                              </div>
                            )}
                          </div>
                          </td>
                        </tr>
                      )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {
        filteredItems.length === 0 && (
          <div className="text-center py-12 bg-white rounded-lg shadow">
            <div className="text-gray-400 text-6xl mb-4">📦</div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              {loading ? 'Loading items...' : searchTerm || selectedCategory || showLowStock ? 'No items match your filters' : 'No items found'}
            </h3>
            <p className="text-gray-600 mb-4">
              {searchTerm || selectedCategory || showLowStock
                ? 'Try adjusting your search or filter criteria'
                : user?.role === 'admin'
                  ? 'Start by adding items to your inventory'
                  : `No items have been distributed to your stall yet (Stall ID: ${user?.stall_id || 'N/A'})`}
            </p>
            <div className="text-xs text-gray-400 mt-2">
              User: {user?.username} | Role: {user?.role} | Mode: {navigator.onLine ? 'Online' : 'Offline'}
            </div>
            {user?.role === 'admin' && !searchTerm && !selectedCategory && !showLowStock && (
              <button
                onClick={() => navigate('/add-item')}
                className="btn-primary mt-4"
              >
                ➕ Add Your First Item
              </button>
            )}
          </div>
        )
      }

      {/* Stock Distribution Modal */}
      {
        showDistributeModal && selectedItem && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white p-6 rounded-lg shadow-lg w-full max-w-2xl max-h-[90vh] overflow-y-auto">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                Distribute Stock - {selectedItem.item_name}
              </h3>
              <form onSubmit={handleDistributionSubmit} className="space-y-4">
                <div className="space-y-2">
                  <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                    <div>
                      <label className="block text-sm font-medium text-gray-700">
                        Available to distribute: {selectedItemAvailable}
                      </label>
                      <p className="text-xs text-gray-500">
                        Total received: {selectedItemReceived} | At stalls (unsold): {selectedItemDistributed} | Sold: {selectedItemSold}
                      </p>
                    </div>
                    <div className="text-xs text-gray-600">
                      Remaining after this plan: {remainingAfterPendingDistribution}
                    </div>
                  </div>
                  <p className="text-xs text-gray-500">
                    Total to distribute: {totalQuantityPendingDistribution} / {selectedItemAvailable}
                  </p>
                  {selectedItemAvailable === 0 && (
                    <div className="rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-700">
                      No more stock is available to distribute. Add stock before allocating to stalls.
                    </div>
                  )}
                </div>

                <div>
                  <div className="flex justify-between items-center mb-2">
                    <label className="block text-sm font-medium text-gray-700">Stall Distributions</label>
                    <button
                      type="button"
                      onClick={addDistributionRow}
                      className="text-sm bg-blue-600 text-white px-3 py-1 rounded hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50"
                      disabled={selectedItemAvailable === 0}
                    >
                      + Add Stall
                    </button>
                  </div>

                  {distributionData.distributions.length === 0 && (
                    <p className="text-sm text-gray-500 italic">Click "Add Stall" to start distributing</p>
                  )}

                  {distributionData.distributions.map((dist, index) => (
                    <div key={index} className="flex gap-2 mb-2 items-end">
                      <div className="flex-1">
                        <label className="block text-xs font-medium text-gray-700 mb-1">Stall</label>
                        <select
                          value={dist.stall_id}
                          onChange={(e) => updateDistribution(index, 'stall_id', e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                          required
                        >
                          <option value="">-- Select stall --</option>
                          {stalls.map(stall => (
                            <option key={stall.stall_id} value={stall.stall_id}>
                              {stall.stall_name} ({stall.manager})
                            </option>
                          ))}
                        </select>
                      </div>
                      <div className="flex-1">
                        <label className="block text-xs font-medium text-gray-700 mb-1">Quantity</label>
                        <input
                          type="number"
                          value={dist.quantity}
                          onChange={(e) => updateDistribution(index, 'quantity', e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                          min="1"
                          max={selectedItem?.current_stock || 0}
                          required
                          placeholder="Qty"
                        />
                      </div>
                      <button
                        type="button"
                        onClick={() => removeDistributionRow(index)}
                        className="px-3 py-2 bg-red-600 text-white rounded hover:bg-red-700"
                      >
                        Remove
                      </button>
                    </div>
                  ))}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Notes (Optional)</label>
                  <textarea
                    value={distributionData.notes}
                    onChange={(e) => setDistributionData(prev => ({ ...prev, notes: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    rows={3}
                    placeholder="Add distribution notes..."
                  />
                </div>

                {/* AI Automation Section */}
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                  <h4 className="text-sm font-semibold text-blue-900 mb-1">🤖 AI Stock Optimization</h4>
                  <p className="text-xs text-blue-700">
                    AI suggests optimal distribution based on sales history and demand patterns.
                  </p>
                  <div className="mt-2 text-xs text-blue-600">
                    💡 Recommended: Distribute 60% to Chuka Town, 40% to Ndagani
                  </div>
                </div>

                <div className="flex justify-end space-x-3">
                  <button
                    type="button"
                    onClick={() => setShowDistributeModal(false)}
                    className="px-4 py-2 text-gray-600 hover:text-gray-800"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={selectedItemAvailable === 0 || totalQuantityPendingDistribution === 0 || isSubmitting}
                    className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-60"
                  >
                    {isSubmitting ? 'Distributing...' : 'Distribute Stock'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )
      }

      {/* Add Stock Modal */}
      {
        showAddStockModal && selectedItem && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white p-6 rounded-lg shadow-lg w-full max-w-md">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Add Stock</h3>
              <form onSubmit={handleAddStockSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Item</label>
                  <input
                    type="text"
                    value={selectedItem.item_name}
                    disabled
                    className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Available for Distribution</label>
                  <input
                    type="text"
                    value={selectedItemAvailable}
                    disabled
                    className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">New Items to Add *</label>
                  <input
                    type="number"
                    value={addStockQuantity}
                    onChange={(e) => setAddStockQuantity(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    min="1"
                    required
                  />
                </div>

                <div className="rounded-md border border-gray-200 bg-gray-50 p-3 text-sm text-gray-700">
                  <div className="flex justify-between">
                    <span>Will be available to distribute</span>
                    <span className="font-semibold">{previewAvailableAfterAdd}</span>
                  </div>
                  <div className="mt-1 flex justify-between">
                    <span>Total received after this addition</span>
                    <span className="font-semibold">{previewTotalReceived}</span>
                  </div>
                  <p className="mt-2 text-xs text-gray-500">
                    Currently at stalls (unsold): {selectedItemDistributed}. Sold so far: {selectedItemSold}.
                  </p>
                </div>

                <div className="flex justify-end space-x-3">
                  <button
                    type="button"
                    onClick={() => {
                      setShowAddStockModal(false);
                      setAddStockQuantity('');
                    }}
                    className="px-4 py-2 text-gray-600 hover:text-gray-800"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 disabled:opacity-60 disabled:cursor-not-allowed"
                  >
                    {isSubmitting ? 'Adding...' : 'Add Stock'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )
      }

      {/* Edit Item Modal */}
      {
        showEditModal && selectedItem && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white p-6 rounded-lg shadow-lg w-full max-w-md">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Edit Item</h3>
              <form onSubmit={handleEditSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Item Name *</label>
                  <input
                    type="text"
                    value={editFormData.item_name}
                    onChange={(e) => setEditFormData(prev => ({ ...prev, item_name: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Category *</label>
                  <input
                    type="text"
                    value={editFormData.category}
                    onChange={(e) => setEditFormData(prev => ({ ...prev, category: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Unit Price (Selling Price) *</label>
                  <input
                    type="number"
                    value={editFormData.unit_price}
                    onChange={(e) => setEditFormData(prev => ({ ...prev, unit_price: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    min="0.01"
                    step="0.01"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Buying Price (Cost) *</label>
                  <input
                    type="number"
                    value={editFormData.buying_price}
                    onChange={(e) => setEditFormData(prev => ({ ...prev, buying_price: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    min="0.01"
                    step="0.01"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Initial Stock *</label>
                  <input
                    type="number"
                    value={editFormData.initial_stock}
                    onChange={(e) => setEditFormData(prev => ({ ...prev, initial_stock: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    min="0"
                    required
                  />
                  <p className="mt-1 text-xs text-gray-500">Original stock quantity when item was added</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Current Stock *</label>
                  <input
                    type="number"
                    value={editFormData.current_stock}
                    onChange={(e) => setEditFormData(prev => ({ ...prev, current_stock: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    min="0"
                    required
                  />
                  <p className="mt-1 text-xs text-gray-500">Adjust to correct on-hand quantity.</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Items Added (Total) *</label>
                  <input
                    type="number"
                    value={editFormData.total_added}
                    onChange={(e) => setEditFormData(prev => ({ ...prev, total_added: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    min="0"
                    required
                  />
                  <p className="mt-1 text-xs text-gray-500">Total quantity added after initial stock</p>
                </div>

                <div className="flex justify-end space-x-3">
                  <button
                    type="button"
                    onClick={() => setShowEditModal(false)}
                    className="px-4 py-2 text-gray-600 hover:text-gray-800"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="bg-purple-600 text-white px-4 py-2 rounded-lg hover:bg-purple-700 disabled:opacity-60 disabled:cursor-not-allowed"
                  >
                    {isSubmitting ? 'Updating...' : 'Update Item'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )
      }
      {/* Edit Distribution Modal */}
      {
        showEditDistModal && editingDist && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white p-6 rounded-lg shadow-lg w-full max-w-md">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Edit Distribution</h3>
              <form onSubmit={handleEditDistSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Stall *</label>
                  <select
                    value={editDistStallId}
                    onChange={(e) => setEditDistStallId(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  >
                    <option value="">Select a stall</option>
                    {stalls.map((stall) => (
                      <option key={stall.stall_id} value={stall.stall_id}>
                        {stall.stall_name}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Quantity *</label>
                  <input
                    type="number"
                    value={editDistQty}
                    onChange={(e) => setEditDistQty(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    min="1"
                    required
                  />
                </div>

                <div className="flex justify-end space-x-3 mt-6">
                  <button
                    type="button"
                    onClick={() => {
                      setShowEditDistModal(false);
                      setEditingDist(null);
                    }}
                    className="px-4 py-2 text-gray-600 hover:text-gray-800"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 disabled:opacity-60 disabled:cursor-not-allowed"
                  >
                    {isSubmitting ? 'Saving...' : 'Save Changes'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )
      }

      {/* Owner Withdrawal Modal */}
      {showWithdrawModal && selectedItem && (() => {
        // Compute per-stall remaining using the same formula as the inventory table:
        //   remaining = allocated_to_stall - stall_sold, reconciled to distributedLive
        const stallSoldForSelected = getItemsSold(selectedItem.item_id, selectedItem.item_name, true);
        const distributedLiveSelected = Math.max(0, (selectedItem.total_allocated || 0) - stallSoldForSelected);

        const allocs = stallAllocMap.get(selectedItem.item_id) || [];
        const byStallId = new Map<number, number>();
        allocs.forEach((a: { stall_id?: number; quantity_allocated: number }) => {
          const sid = Number(a.stall_id);
          if (!sid) return;
          byStallId.set(sid, (byStallId.get(sid) || 0) + (a.quantity_allocated || 0));
        });

        const rawStallEntries = stalls
          .filter(s => byStallId.has(s.stall_id))
          .map(s => ({
            stall_id: s.stall_id,
            name: s.stall_name,
            rawRemaining: Math.max(0, (byStallId.get(s.stall_id) || 0) - getStallSold(selectedItem.item_id, s.stall_id))
          }));

        const reconciled = reconcileStallRemainings(
          rawStallEntries.map(e => ({ name: e.name, rawRemaining: e.rawRemaining })),
          distributedLiveSelected
        );

        const stallOptions = rawStallEntries.map((e, i) => ({
          stall_id: e.stall_id,
          stall_name: e.name,
          total: reconciled[i]?.remaining ?? 0
        }));

        const selectedStall = withdrawSource !== 'central'
          ? stallOptions.find((s) => String(s.stall_id) === withdrawSource)
          : null;
        const availableQty = withdrawSource === 'central'
          ? (selectedItem.current_stock ?? 0)
          : (selectedStall?.total ?? 0);
        const isInsufficient = availableQty <= 0;

        return (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white p-6 rounded-lg shadow-lg w-full max-w-md">
              <h3 className="text-lg font-semibold text-gray-900 mb-1">📦 Stock Withdrawal</h3>
              <p className="text-sm text-gray-500 mb-4">
                Choose where to withdraw stock from, then enter the quantity.
              </p>
              <form onSubmit={handleWithdrawSubmit} className="space-y-4">

                {/* Item info */}
                <div className="bg-orange-50 border border-orange-200 rounded-lg p-3">
                  <p className="text-sm text-orange-800">
                    <strong>Item:</strong> {selectedItem.item_name}
                  </p>
                  <p className="text-sm text-orange-800">
                    <strong>Available ({withdrawSource === 'central' ? 'Central Hub' : (selectedStall?.stall_name ?? 'Stall')}):</strong>{' '}
                    <span className={isInsufficient ? 'text-red-600 font-bold' : 'font-bold'}>
                      {availableQty}
                    </span>
                  </p>
                </div>

                {/* Source dropdown */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Withdraw From *
                  </label>
                  <select
                    value={withdrawSource}
                    onChange={(e) => {
                      setWithdrawSource(e.target.value);
                      setWithdrawQuantity('');
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
                  >
                    <option value="central">
                      🏠 Central Hub — {selectedItem.current_stock ?? 0} available
                    </option>
                    {stallOptions.map((s) => (
                      <option key={s.stall_id} value={String(s.stall_id)}>
                        🏪 {s.stall_name} — {s.total} available
                      </option>
                    ))}
                  </select>
                </div>

                {/* Insufficient stock warning */}
                {isInsufficient && (
                  <div className="bg-red-50 border border-red-300 rounded-lg p-3">
                    <p className="text-sm text-red-700 font-medium">
                      ⛔ No stock available at this location. Select a different source or add stock first.
                    </p>
                  </div>
                )}

                {/* Quantity */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Quantity to Withdraw *
                  </label>
                  <input
                    type="number"
                    value={withdrawQuantity}
                    onChange={(e) => setWithdrawQuantity(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
                    min="1"
                    max={availableQty}
                    required
                    disabled={isInsufficient}
                    placeholder={isInsufficient ? 'No stock available' : 'Enter quantity'}
                  />
                </div>

                {/* Comment — required so the client can recall why stock left */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Your comment *
                  </label>
                  <input
                    type="text"
                    value={withdrawReason}
                    onChange={(e) => setWithdrawReason(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
                    placeholder="E.g., Personal use, Gift, Damaged, Stock correction"
                    required
                    maxLength={200}
                    disabled={isInsufficient}
                  />
                  <p className="mt-1 text-xs text-gray-500">
                    Saved on the withdrawal record so you can see why stock left later.
                  </p>
                </div>

                {/* Live preview — uses the same “stock available before/after” language */}
                {!isInsufficient && Number(withdrawQuantity) > 0 && (
                  <div className="bg-orange-50 border border-orange-200 rounded-lg p-3">
                    <p className="text-sm text-orange-900 font-medium">
                      {withdrawSource === 'central'
                        ? `Remove ${withdrawQuantity} from Central Hub${withdrawReason.trim() ? ` (${withdrawReason.trim()})` : ''}. Stock available before withdrawal: ${availableQty} → after: ${Math.max(0, availableQty - Number(withdrawQuantity))}.`
                        : `Return ${withdrawQuantity} from ${selectedStall?.stall_name ?? 'stall'} to Central Hub${withdrawReason.trim() ? ` (${withdrawReason.trim()})` : ''}. Stock available before withdrawal: ${availableQty} → after: ${Math.max(0, availableQty - Number(withdrawQuantity))}.`}
                    </p>
                  </div>
                )}

                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                  <p className="text-xs text-yellow-800">
                    {withdrawSource === 'central'
                      ? 'You will be asked to confirm before this runs. Taking from Central Hub permanently removes that stock from inventory.'
                      : 'You will be asked to confirm before this runs. This returns stock from the stall to Central Hub (it is not deleted).'}
                  </p>
                </div>

                <div className="flex justify-end space-x-3 mt-6">
                  <button
                    type="button"
                    onClick={() => {
                      setShowWithdrawModal(false);
                      setWithdrawQuantity('');
                      setWithdrawReason('');
                      setWithdrawSource('central');
                      setWithdrawItemDistributions([]);
                    }}
                    className="px-4 py-2 text-gray-600 hover:text-gray-800"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={isSubmitting || isInsufficient || !withdrawReason.trim()}
                    className="bg-orange-600 text-white px-4 py-2 rounded-lg hover:bg-orange-700 disabled:opacity-60 disabled:cursor-not-allowed"
                  >
                    {isSubmitting ? 'Processing...' : 'Confirm Withdrawal'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        );
      })()}

      {/* Withdraw from Distribution Modal */}
      {
        showWithdrawFromDistModal && withdrawFromDist && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white p-6 rounded-lg shadow-lg w-full max-w-md">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                Return stock from stall to central
              </h3>
              <p className="text-sm text-gray-600 mb-4">
                Withdraws from <strong>{withdrawFromDist.stall_name}</strong> and returns units to the central hub. Recorded in history under that stall name.
              </p>
              <form onSubmit={handleWithdrawFromDistSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    User/Stall
                  </label>
                  <input
                    type="text"
                    value={withdrawFromDist.stall_name}
                    disabled
                    className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Currently Distributed
                  </label>
                  <input
                    type="text"
                    value={withdrawFromDist.quantity_allocated}
                    disabled
                    className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50"
                  />
                </div>

                {(() => {
                  const stallSold =
                    salesAggregates.byItemStall[
                      `${withdrawFromDist.item_id}-${withdrawFromDist.stall_id}`
                    ] ?? 0;
                  const sameStallBatches = itemDistributions.filter(
                    (d: any) =>
                      Number(d.item_id) === Number(withdrawFromDist.item_id) &&
                      Number(d.stall_id) === Number(withdrawFromDist.stall_id)
                  );
                  const totalAllocated = sameStallBatches.reduce(
                    (sum: number, d: any) => sum + (Number(d.quantity_allocated) || 0),
                    0
                  );
                  const stallLeft = Math.max(0, totalAllocated - stallSold);
                  const maxReturnable = Math.min(
                    Number(withdrawFromDist.quantity_allocated) || 0,
                    stallLeft
                  );
                  return (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Quantity to Withdraw *
                      </label>
                      <input
                        type="number"
                        value={withdrawFromDistQty}
                        onChange={(e) => setWithdrawFromDistQty(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
                        min="1"
                        max={maxReturnable}
                        required
                        placeholder="Enter quantity to withdraw"
                      />
                      <p className="mt-1 text-xs text-gray-500">
                        Max returnable: {maxReturnable} (unsold at stall). Distributed batch shows{' '}
                        {withdrawFromDist.quantity_allocated}.
                      </p>
                    </div>
                  );
                })()}

                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                  <p className="text-xs text-yellow-800">
                    You will be asked to confirm. This returns stock from {withdrawFromDist.stall_name} to Central Hub (it is not deleted).
                  </p>
                </div>

                <div className="flex justify-end space-x-3 mt-6">
                  <button
                    type="button"
                    onClick={() => {
                      setShowWithdrawFromDistModal(false);
                      setWithdrawFromDist(null);
                      setWithdrawFromDistQty('');
                    }}
                    className="px-4 py-2 text-gray-600 hover:text-gray-800"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="bg-orange-600 text-white px-4 py-2 rounded-lg hover:bg-orange-700 disabled:opacity-60 disabled:cursor-not-allowed"
                  >
                    {isSubmitting ? 'Processing...' : 'Confirm return'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )
      }

      {/* Undo toast — appears after a successful withdrawal from distribution */}
      {undoToast && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 flex items-center gap-4 bg-gray-900 text-white px-5 py-3 rounded-xl shadow-2xl animate-fade-in">
          <span className="text-sm">
            ✅ Withdrew <strong>{undoToast.qty}</strong> item(s) from <strong>{undoToast.stallName}</strong>. Returned to central hub.
          </span>
          <button
            onClick={handleUndoWithdrawal}
            className="bg-orange-500 hover:bg-orange-400 text-white text-sm font-semibold px-3 py-1.5 rounded-lg transition-colors"
          >
            ↩ Undo
          </button>
          <button
            onClick={() => {
              if (undoTimerRef.current) clearTimeout(undoTimerRef.current);
              setUndoToast(null);
            }}
            className="text-gray-400 hover:text-white text-lg leading-none"
            aria-label="Dismiss"
          >
            ×
          </button>
        </div>
      )}
    </div>
  );
};

export default Inventory;
